export const KERNEL_RUN_OUTCOME = Object.freeze({
    COMPLETED: "completed",
    EMPTY: "empty",
    UNAVAILABLE: "unavailable",
    REFUSED: "refused",
    CANCELLED: "cancelled",
    FAILED: "failed",
});

export const DEFAULT_KERNEL_RUN_TIMEOUT_MS = 60_000;

export const KERNEL_RUN_REFUSAL_REASON = Object.freeze({
    SCHEDULER_OVERLOADED: "scheduler-overloaded",
});

// Closed, bounded values used by the per-run observer/telemetry seam. Do not
// put commands, ids, errors, or other unbounded values in this field.
export const KERNEL_REPORTED_OUTCOME = Object.freeze({
    SUCCESS: "success",
    EMPTY: KERNEL_RUN_OUTCOME.EMPTY,
    UNAVAILABLE: KERNEL_RUN_OUTCOME.UNAVAILABLE,
    REFUSED: KERNEL_RUN_OUTCOME.REFUSED,
    FAILED: KERNEL_RUN_OUTCOME.FAILED,
    CANCELLED: KERNEL_RUN_OUTCOME.CANCELLED,
});

export const KERNEL_REPORTED_REASON_CATEGORY = Object.freeze({
    NONE: "none",
    INFRASTRUCTURE: "infrastructure",
    SCHEDULER: "scheduler",
    CONTENT: "content",
});

export const KERNEL_RUN_CANCEL_REASON = Object.freeze({
    ABORTED: "aborted",
    USER_STOP: "user-stop",
    QUEUED_CANCELLED: "queued-cancelled",
    TIMEOUT: "timeout",
    SUPERSEDED: "superseded",
    UNMOUNTED: "unmounted",
    SCHEDULER_DISPOSED: "scheduler-disposed",
    COLLATERAL_REALM_TEARDOWN: "collateral-realm-teardown",
});

const DEFAULT_CANCEL_MESSAGES = Object.freeze({
    [KERNEL_RUN_CANCEL_REASON.ABORTED]: "Run cancelled.",
    [KERNEL_RUN_CANCEL_REASON.USER_STOP]: "Run stopped by the viewer.",
    [KERNEL_RUN_CANCEL_REASON.QUEUED_CANCELLED]: "Queued run cancelled before it started.",
    [KERNEL_RUN_CANCEL_REASON.TIMEOUT]: "Run exceeded its wall-clock limit.",
    [KERNEL_RUN_CANCEL_REASON.SUPERSEDED]: "Run replaced by a newer request.",
    [KERNEL_RUN_CANCEL_REASON.UNMOUNTED]: "Run cancelled when its view closed.",
    [KERNEL_RUN_CANCEL_REASON.SCHEDULER_DISPOSED]: "Run cancelled when its canvas closed.",
    [KERNEL_RUN_CANCEL_REASON.COLLATERAL_REALM_TEARDOWN]:
        "Run cancelled when another run caused the shared Python realm to restart.",
});

export class KernelRunCancelledError extends Error {
    constructor(reason) {
        const normalized = normalizeKernelRunCancelReason(reason);
        super(normalized.message);
        this.name = "KernelRunCancelledError";
        this.cancelled = true;
        this.reason = normalized;
    }
}

export class KernelRunOverloadedError extends Error {
    constructor(refusal) {
        super(refusal.message);
        this.name = "KernelRunOverloadedError";
        this.code = refusal.reasonCode;
        this.category = refusal.reasonCategory;
        this.overloaded = true;
        this.refusal = refusal;
    }
}

export function normalizeKernelRunCancelReason(reason, fallbackCode = KERNEL_RUN_CANCEL_REASON.ABORTED) {
    if (reason instanceof KernelRunCancelledError) return reason.reason;
    if (reason?.reason && reason.cancelled === true) {
        return normalizeKernelRunCancelReason(reason.reason, fallbackCode);
    }
    const raw = reason && typeof reason === "object" ? reason : {};
    const code = typeof raw.code === "string" && raw.code
        ? raw.code
        : typeof reason === "string" && reason
            ? reason
            : fallbackCode;
    return Object.freeze({
        ...raw,
        code,
        category: "cancelled",
        message: typeof raw.message === "string" && raw.message
            ? raw.message
            : DEFAULT_CANCEL_MESSAGES[code] || DEFAULT_CANCEL_MESSAGES[fallbackCode],
    });
}

export function kernelRunCancelReasonFromSignal(signal, fallbackCode) {
    if (!signal?.aborted) return null;
    return normalizeKernelRunCancelReason(signal.reason, fallbackCode);
}

export function isKernelRunCancellation(error, signal = null) {
    return Boolean(
        error instanceof KernelRunCancelledError
        || error?.cancelled === true
        || error?.name === "AbortError"
        || signal?.aborted,
    );
}

export function cancelledKernelRunResult(reason, extra = {}) {
    const normalized = normalizeKernelRunCancelReason(reason);
    const error = extra.error instanceof Error
        ? extra.error
        : new KernelRunCancelledError(normalized);
    return {
        ...extra,
        ok: false,
        outcome: KERNEL_RUN_OUTCOME.CANCELLED,
        cancelled: true,
        reason: normalized,
        error,
    };
}

export function failedKernelRunResult(error, extra = {}) {
    const normalizedError = error instanceof Error ? error : new Error(String(error || "kernel run failed"));
    const reason = extra.reason && typeof extra.reason === "object"
        ? extra.reason
        : {
            code: "run-failed",
            category: "failed",
            message: normalizedError.message,
        };
    return {
        ...extra,
        ok: false,
        outcome: KERNEL_RUN_OUTCOME.FAILED,
        cancelled: false,
        reason,
        error: normalizedError,
    };
}

export function overloadedKernelRunResult({
    limit,
    admitted,
    runId = null,
    message = null,
} = {}) {
    const refusal = Object.freeze({
        code: KERNEL_RUN_REFUSAL_REASON.SCHEDULER_OVERLOADED,
        category: "refused",
        reasonCode: KERNEL_RUN_REFUSAL_REASON.SCHEDULER_OVERLOADED,
        reasonCategory: "capacity",
        message: message
            || `Run was not admitted because this canvas already has ${limit} active or waiting runs.`,
        limit,
        admitted,
    });
    return {
        ok: false,
        outcome: KERNEL_RUN_OUTCOME.REFUSED,
        refused: true,
        overloaded: true,
        reasonCode: refusal.reasonCode,
        reasonCategory: refusal.reasonCategory,
        reason: refusal,
        refusal,
        error: new KernelRunOverloadedError(refusal),
        analyticsQueryCount: 0,
        ...(runId ? { runId } : {}),
    };
}

export function kernelRunOutcomeOf(result) {
    if (typeof result?.outcome === "string" && result.outcome) return result.outcome;
    if (result?.cancelled) return KERNEL_RUN_OUTCOME.CANCELLED;
    return result?.ok ? KERNEL_RUN_OUTCOME.COMPLETED : KERNEL_RUN_OUTCOME.FAILED;
}

export function reportedKernelRunOutcomeOf(result) {
    const outcome = kernelRunOutcomeOf(result);
    if (outcome === KERNEL_RUN_OUTCOME.COMPLETED) {
        return KERNEL_REPORTED_OUTCOME.SUCCESS;
    }
    if (Object.values(KERNEL_REPORTED_OUTCOME).includes(outcome)) {
        return outcome;
    }
    return result?.ok
        ? KERNEL_REPORTED_OUTCOME.SUCCESS
        : KERNEL_REPORTED_OUTCOME.FAILED;
}

export function reportedKernelRunReasonCategoryOf(result) {
    const outcome = reportedKernelRunOutcomeOf(result);
    if (outcome === KERNEL_REPORTED_OUTCOME.SUCCESS) {
        return KERNEL_REPORTED_REASON_CATEGORY.NONE;
    }
    if (outcome === KERNEL_REPORTED_OUTCOME.EMPTY) {
        return KERNEL_REPORTED_REASON_CATEGORY.CONTENT;
    }
    if (outcome === KERNEL_REPORTED_OUTCOME.UNAVAILABLE) {
        return KERNEL_REPORTED_REASON_CATEGORY.INFRASTRUCTURE;
    }
    if (outcome === KERNEL_REPORTED_OUTCOME.CANCELLED) {
        return KERNEL_REPORTED_REASON_CATEGORY.SCHEDULER;
    }

    const detailedCategory = result?.reasonCategory
        || result?.refusal?.reasonCategory
        || result?.reason?.reasonCategory
        || result?.reason?.category;
    if (["runtime-capability", "infrastructure", "unavailable"].includes(detailedCategory)) {
        return KERNEL_REPORTED_REASON_CATEGORY.INFRASTRUCTURE;
    }
    if (["capacity", "scheduler", "cancelled"].includes(detailedCategory)) {
        return KERNEL_REPORTED_REASON_CATEGORY.SCHEDULER;
    }
    return KERNEL_REPORTED_REASON_CATEGORY.CONTENT;
}
